import { memo } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';

import { withSentryReactRouterRouting } from '@portal/common/services/sentry';
import lazyWithRetry from '@portal/common/utility/lazy-with-retry';

import AiInsightsRoute from './AiInsightsRoute';
import AnonymousRoute from './AnonymousRoute';
import ProtectedRoute from './ProtectedRoute';
import {
    CATEGORIES_PATH,
    CONTENT_MANAGEMENT_METHODOLOGY_PATH,
    CONTENT_MANAGEMENT_NEWS_ANNOUNCEMENTS_PATH,
    CONTENT_MANAGEMENT_SIDEBAR_LINKS_PATH,
    CONTENT_MANAGEMENT_WELCOME_MESSAGE_PATH,
    DATA_COLLECTIONS_CREATE_PATH,
    DATA_COLLECTIONS_EDIT_PATH,
    DATA_COLLECTIONS_PATH,
    FILES_PATH,
    FORGOT_PASSWORD_PATH,
    HOME_PATH,
    INSIGHTS_AI_CHAT_DETAIL_PATH,
    INSIGHTS_AI_FEEDBACK_DETAIL_PATH,
    INSIGHTS_TAB_PATH,
    LINKS_PATH,
    ORGANIZATIONS_PATH,
    ORGANIZATION_COLLECTIONS_PATH,
    ORGANIZATION_FILES_PATH,
    ORGANIZATION_LINKS_PATH,
    ORGANIZATION_MEMBERS_PATH,
    ORGANIZATION_PROFILE_PATH,
    ORGANIZATION_PUBLIC_LISTS_PATH,
    ORGANIZATION_SETTINGS_PATH,
    ORGANIZATION_WARNING_MESSAGE_PATH,
    ORGANIZATION_WELCOME_MESSAGE_PATH,
    PROFILE_PATH,
    SETTINGS_SMART_OPTIONS_PATH,
    SIGN_IN_PATH,
    STAFF_PATH,
    SURVEYS_PATH,
    SURVEY_CREATE_PATH,
    SURVEY_EDIT_PATH,
} from './paths';

const ActivityScene = lazyWithRetry(() => import('../scenes/ActivityScene'));
const ContentCategoriesScene = lazyWithRetry(() => import('../scenes/ContentCategoriesScene'));
const ContentFilesScene = lazyWithRetry(() => import('../scenes/ContentFilesScene'));
const ContentLinksScene = lazyWithRetry(() => import('../scenes/ContentLinksScene'));
const MethodologyScene = lazyWithRetry(
    () => import('../scenes/ContentManagementScene/MethodologyScene')
);
const NewsAndAnnouncementsScene = lazyWithRetry(
    () => import('../scenes/ContentManagementScene/NewsAndAnnouncementsScene')
);
const SidebarLinksScene = lazyWithRetry(
    () => import('../scenes/ContentManagementScene/SidebarLinksScene')
);
const WelcomeMessageScene = lazyWithRetry(
    () => import('../scenes/ContentManagementScene/WelcomeMessageScene')
);
const DataCollectionsCreateEditScene = lazyWithRetry(
    () => import('../scenes/DataCollectionCreateEditScene')
);
const DataCollectionsScene = lazyWithRetry(() => import('../scenes/DataCollectionsScene'));
const ForgotPasswordScene = lazyWithRetry(() => import('../scenes/ForgotPasswordScene'));
const InsightsScene = lazyWithRetry(() => import('../scenes/InsightsScene'));
const AiFeedbackDetailScene = lazyWithRetry(
    () => import('../scenes/InsightsScene/AiFeedbackDetailScene')
);
const OrganizationCollectionsScene = lazyWithRetry(
    () => import('../scenes/OrganizationCollectionsScene')
);
const OrganizationFilesScene = lazyWithRetry(() => import('../scenes/OrganizationFilesScene'));
const OrganizationLicenseExpirationScene = lazyWithRetry(
    () => import('../scenes/OrganizationLicenseExpirationScene')
);
const OrganizationLinksScene = lazyWithRetry(() => import('../scenes/OrganizationLinksScene'));
const OrganizationMembersScene = lazyWithRetry(() => import('../scenes/OrganizationMembersScene'));
const OrganizationProfileScene = lazyWithRetry(() => import('../scenes/OrganizationProfileScene'));
const OrganizationPublicListsScene = lazyWithRetry(
    () => import('../scenes/OrganizationPublicListsScene')
);
const OrganizationSettingsScene = lazyWithRetry(
    () => import('../scenes/OrganizationSettingsScene')
);
const OrganizationWelcomeMessageScene = lazyWithRetry(
    () => import('../scenes/OrganizationWelcomeMessageScene')
);
const OrganizationsScene = lazyWithRetry(() => import('../scenes/OrganizationsScene'));
const ProfileScene = lazyWithRetry(() => import('../scenes/ProfileScene'));
const SmartOptionsScene = lazyWithRetry(() => import('../scenes/SettingsScene/SmartOptionsScene'));
const SignInScene = lazyWithRetry(() => import('../scenes/SignInScene'));
const StaffDirectoryScene = lazyWithRetry(() => import('../scenes/StaffDirectoryScene'));
const SurveyEditorScene = lazyWithRetry(() => import('../scenes/SurveyEditorScene'));
const SurveysScene = lazyWithRetry(() => import('../scenes/SurveysScene'));

const SentryRoutes = withSentryReactRouterRouting(Routes);

export default memo(() => (
    <BrowserRouter unstable_useTransitions={false} future={{ v7_relativeSplatPath: true }}>
        <SentryRoutes>
            <Route element={<AnonymousRoute />}>
                {/* Unauthenticated User Routes */}
                <Route path={SIGN_IN_PATH} element={<SignInScene />} />
                <Route path={FORGOT_PASSWORD_PATH} element={<ForgotPasswordScene />} />
            </Route>

            <Route element={<ProtectedRoute />}>
                {/* Authenticated User Routes */}
                <Route path={HOME_PATH} element={<ActivityScene />} />
                <Route path={INSIGHTS_TAB_PATH} element={<InsightsScene />} />
                <Route element={<AiInsightsRoute />}>
                    <Route
                        path={INSIGHTS_AI_CHAT_DETAIL_PATH}
                        element={<AiFeedbackDetailScene />}
                    />
                    <Route
                        path={INSIGHTS_AI_FEEDBACK_DETAIL_PATH}
                        element={<AiFeedbackDetailScene />}
                    />
                </Route>
                <Route path={ORGANIZATIONS_PATH} element={<OrganizationsScene />} />
                <Route path={ORGANIZATION_PROFILE_PATH} element={<OrganizationProfileScene />} />
                <Route path={ORGANIZATION_MEMBERS_PATH} element={<OrganizationMembersScene />} />
                <Route path={ORGANIZATION_LINKS_PATH} element={<OrganizationLinksScene />} />
                <Route path={ORGANIZATION_FILES_PATH} element={<OrganizationFilesScene />} />
                <Route
                    path={ORGANIZATION_COLLECTIONS_PATH}
                    element={<OrganizationCollectionsScene />}
                />
                <Route
                    path={ORGANIZATION_PUBLIC_LISTS_PATH}
                    element={<OrganizationPublicListsScene />}
                />
                <Route
                    path={ORGANIZATION_WELCOME_MESSAGE_PATH}
                    element={<OrganizationWelcomeMessageScene />}
                />
                <Route
                    path={ORGANIZATION_WARNING_MESSAGE_PATH}
                    element={<OrganizationLicenseExpirationScene />}
                />
                <Route path={ORGANIZATION_SETTINGS_PATH} element={<OrganizationSettingsScene />} />
                <Route path={DATA_COLLECTIONS_PATH} element={<DataCollectionsScene />} />
                <Route
                    path={CONTENT_MANAGEMENT_WELCOME_MESSAGE_PATH}
                    element={<WelcomeMessageScene />}
                />
                <Route path={SURVEYS_PATH} element={<SurveysScene />} />
                <Route path={SURVEY_CREATE_PATH} element={<SurveyEditorScene />} />
                <Route path={SURVEY_EDIT_PATH} element={<SurveyEditorScene />} />
                <Route
                    path={CONTENT_MANAGEMENT_NEWS_ANNOUNCEMENTS_PATH}
                    element={<NewsAndAnnouncementsScene />}
                />
                <Route
                    path={CONTENT_MANAGEMENT_SIDEBAR_LINKS_PATH}
                    element={<SidebarLinksScene />}
                />
                <Route path={CONTENT_MANAGEMENT_METHODOLOGY_PATH} element={<MethodologyScene />} />
                <Route
                    path={DATA_COLLECTIONS_CREATE_PATH}
                    element={<DataCollectionsCreateEditScene />}
                />
                <Route
                    path={DATA_COLLECTIONS_EDIT_PATH}
                    element={<DataCollectionsCreateEditScene />}
                />
                <Route path={LINKS_PATH} element={<ContentLinksScene />} />
                <Route path={FILES_PATH} element={<ContentFilesScene />} />
                <Route path={CATEGORIES_PATH} element={<ContentCategoriesScene />} />
                <Route path={PROFILE_PATH} element={<ProfileScene />} />
                <Route path={STAFF_PATH} element={<StaffDirectoryScene />} />
                <Route path={SETTINGS_SMART_OPTIONS_PATH} element={<SmartOptionsScene />} />
                <Route path="*" element={<Navigate to={HOME_PATH} replace />} />
            </Route>
        </SentryRoutes>
    </BrowserRouter>
));
